import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import CommentIcon from '@mui/icons-material/CommentOutlined';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlatOutlined';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoffOutlined';
import { useAuth } from '../../context/authContext';
import {
  addApplicationRemark,
  assignApplication,
  getApplication,
  getApplicationDocumentChecklist,
  getApplicationTimeline,
  getReadyToFlyEligibility,
  listApplicationRemarks,
  updateApplication,
} from '../../api/academic';
import { getTravelProfile, updateTravelDetail } from '../../api/readyToFly';
import { listLookup } from '../../api/masterData';
import { listUsers } from '../../api/users';
import { ApplicationTimelineEntry } from '../../types/academic';
import { TravelDetailInput } from '../../types/readyToFly';

const toDateInputValue = (iso: string | null) => (iso ? iso.slice(0, 10) : '');
const toDateTimeInputValue = (iso: string | null) => (iso ? iso.slice(0, 16) : '');

const FLIGHT_STATUS_OPTIONS = ['PENDING', 'BOOKED', 'CONFIRMED'] as const;
const DEPARTURE_STATUS_OPTIONS = ['PLANNED', 'CONFIRMED', 'DEPARTED', 'ARRIVED'] as const;

const formatDate = (iso: string) => new Date(iso).toLocaleString();

const TimelineItem = ({ entry }: { entry: ApplicationTimelineEntry }) => {
  if (entry.kind === 'STATUS_CHANGE') {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 1.5 }}>
        <TrendingFlatIcon color="secondary" fontSize="small" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">
            {entry.data.fromStatus ? `${entry.data.fromStatus.replace(/_/g, ' ')} → ` : ''}
            {entry.data.toStatus.replace(/_/g, ' ')} by {entry.data.changedBy.firstName}{' '}
            {entry.data.changedBy.lastName}
          </Typography>
          {entry.data.remark && (
            <Typography variant="caption" color="text.secondary">
              {entry.data.remark}
            </Typography>
          )}
          <Typography variant="caption" display="block" color="text.secondary">
            {formatDate(entry.at)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (entry.kind === 'ASSIGNMENT') {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 1.5 }}>
        <PersonIcon color="action" fontSize="small" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">
            {entry.data.fromUser ? `${entry.data.fromUser.firstName} ${entry.data.fromUser.lastName}` : 'Unassigned'}
            {' → '}
            {entry.data.toUser ? `${entry.data.toUser.firstName} ${entry.data.toUser.lastName}` : 'Unassigned'}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            {formatDate(entry.at)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 1.5 }}>
      <CommentIcon color="action" fontSize="small" />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2">{entry.data.remark}</Typography>
        <Typography variant="caption" color="text.secondary">
          {entry.data.createdBy.firstName} {entry.data.createdBy.lastName} — {formatDate(entry.at)}
        </Typography>
      </Box>
    </Paper>
  );
};

interface ApplicationDetailsDialogProps {
  applicationId: number | null;
  onClose: () => void;
}

export const ApplicationDetailsDialog = ({ applicationId, onClose }: ApplicationDetailsDialogProps) => {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [remarkText, setRemarkText] = useState('');
  const [assigneeId, setAssigneeId] = useState<number | ''>('');
  const [offerLetterDate, setOfferLetterDate] = useState('');
  const [coeNumber, setCoeNumber] = useState('');
  const [coeDate, setCoeDate] = useState('');
  const [milestoneError, setMilestoneError] = useState<string | null>(null);

  // Travel & Departure section (Phase 65)
  const [visaStatusId, setVisaStatusId] = useState<number | ''>('');
  const [visaApprovalDate, setVisaApprovalDate] = useState('');
  const [visaExpiryDate, setVisaExpiryDate] = useState('');
  const [visaRemarks, setVisaRemarks] = useState('');
  const [departureDateTime, setDepartureDateTime] = useState('');
  const [travelForm, setTravelForm] = useState<TravelDetailInput>({});
  const [travelError, setTravelError] = useState<string | null>(null);

  const open = applicationId !== null;
  const canAssign = hasPermission('academic', 'assign');
  const canRemark = hasPermission('academic', 'create');
  const canEditMilestones = hasPermission('academic', 'update');
  const canManageTravel = hasPermission('ready_to_fly', 'manage');

  const applicationQuery = useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => getApplication(applicationId!),
    enabled: open,
  });

  const travelProfileQuery = useQuery({
    queryKey: ['travel-profile', applicationId],
    queryFn: () => getTravelProfile(applicationId!),
    enabled: open,
  });

  const visaStatusesQuery = useQuery({
    queryKey: ['lookup', '/master-data/visa-statuses'],
    queryFn: () => listLookup('/master-data/visa-statuses'),
    enabled: open,
  });

  useEffect(() => {
    if (!applicationQuery.data) return;
    setOfferLetterDate(toDateInputValue(applicationQuery.data.offerLetterDate));
    setCoeNumber(applicationQuery.data.coeNumber ?? '');
    setCoeDate(toDateInputValue(applicationQuery.data.coeDate));
    setVisaStatusId(applicationQuery.data.visaStatus?.id ?? '');
    setVisaApprovalDate(toDateInputValue(applicationQuery.data.visaDecisionDate));
    setVisaExpiryDate(toDateInputValue(applicationQuery.data.visaExpiryDate));
    setVisaRemarks(applicationQuery.data.visaRemarks ?? '');
    setDepartureDateTime(toDateTimeInputValue(applicationQuery.data.departureDate));
  }, [applicationQuery.data]);

  useEffect(() => {
    if (!travelProfileQuery.data) return;
    const detail = travelProfileQuery.data.travelDetail;
    setTravelForm({
      airline: detail.airline ?? '',
      flightNumber: detail.flightNumber ?? '',
      departureAirport: detail.departureAirport ?? '',
      arrivalAirport: detail.arrivalAirport ?? '',
      bookingReference: detail.bookingReference ?? '',
      flightStatus: detail.flightStatus,
      departureStatus: detail.departureStatus,
      departureRemarks: detail.departureRemarks ?? '',
      pickupRequired: detail.pickupRequired,
      pickupAssigned: detail.pickupAssigned,
      pickupAssignedTo: detail.pickupAssignedTo ?? '',
      pickupNotes: detail.pickupNotes ?? '',
    });
  }, [travelProfileQuery.data]);

  const timelineQuery = useQuery({
    queryKey: ['application-timeline', applicationId],
    queryFn: () => getApplicationTimeline(applicationId!),
    enabled: open,
  });
  const remarksQuery = useQuery({
    queryKey: ['application-remarks', applicationId],
    queryFn: () => listApplicationRemarks(applicationId!),
    enabled: open,
  });
  const checklistQuery = useQuery({
    queryKey: ['application-checklist', applicationId],
    queryFn: () => getApplicationDocumentChecklist(applicationId!),
    enabled: open,
  });
  const eligibilityQuery = useQuery({
    queryKey: ['application-eligibility', applicationId],
    queryFn: () => getReadyToFlyEligibility(applicationId!),
    enabled: open,
  });
  const usersQuery = useQuery({
    queryKey: ['users-for-assign'],
    queryFn: () => listUsers({ page: 1, pageSize: 100, isActive: true }),
    enabled: open && canAssign,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['application-timeline', applicationId] });
    queryClient.invalidateQueries({ queryKey: ['application-remarks', applicationId] });
    queryClient.invalidateQueries({ queryKey: ['applications'] });
  };

  const addRemarkMutation = useMutation({
    mutationFn: () => addApplicationRemark(applicationId!, remarkText),
    onSuccess: () => {
      setRemarkText('');
      invalidateAll();
    },
  });

  const assignMutation = useMutation({
    mutationFn: (userId: number) => assignApplication(applicationId!, userId),
    onSuccess: () => {
      setAssigneeId('');
      invalidateAll();
    },
  });

  // Batch/admission progress correction (Phase 64) — Offer Letter and
  // Invitation (COE) previously had no editing UI anywhere at all
  // (StudentAcademicTab only showed them read-only); reuses the existing
  // updateApplication API, which now also records a Timeline event for
  // every field that actually changes.
  const milestoneMutation = useMutation({
    mutationFn: () =>
      updateApplication(applicationId!, {
        offerLetterDate: offerLetterDate || null,
        coeNumber: coeNumber.trim() || null,
        coeDate: coeDate || null,
      }),
    onSuccess: () => {
      setMilestoneError(null);
      queryClient.invalidateQueries({ queryKey: ['application', applicationId] });
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: ['ready-to-fly'] });
      queryClient.invalidateQueries({ queryKey: ['travel-batches'] });
    },
    onError: (err) =>
      setMilestoneError(isAxiosError(err) ? (err.response?.data?.message ?? 'Failed to save') : 'Failed to save'),
  });

  // Travel & Departure section (Phase 65) — reuses the existing
  // TravelDetail/Application APIs (`updateTravelDetail`/`updateApplication`),
  // the same combined-save pattern TravelDetailDialog.tsx already
  // establishes, never a new endpoint/module. Every field changed here is
  // recorded in the student's Timeline by the service layer itself.
  const travelDetailsMutation = useMutation({
    mutationFn: async () => {
      if (canManageTravel) {
        await updateTravelDetail(applicationId!, travelForm);
      }
      if (canEditMilestones) {
        await updateApplication(applicationId!, {
          visaStatusId: visaStatusId === '' ? null : Number(visaStatusId),
          visaDecisionDate: visaApprovalDate || null,
          visaExpiryDate: visaExpiryDate || null,
          visaRemarks: visaRemarks.trim() || null,
          departureDate: departureDateTime || null,
        });
      }
    },
    onSuccess: () => {
      setTravelError(null);
      queryClient.invalidateQueries({ queryKey: ['application', applicationId] });
      queryClient.invalidateQueries({ queryKey: ['travel-profile', applicationId] });
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: ['ready-to-fly'] });
      queryClient.invalidateQueries({ queryKey: ['travel-batches'] });
      queryClient.invalidateQueries({ queryKey: ['journey'] });
    },
    onError: (err) =>
      setTravelError(isAxiosError(err) ? (err.response?.data?.message ?? 'Failed to save') : 'Failed to save'),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Application details</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pb: 1 }}>
          {canAssign && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Assign counselor
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Counselor"
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(Number(e.target.value))}
                >
                  {usersQuery.data?.data.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.firstName} {u.lastName} ({u.role.name})
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  disabled={!assigneeId || assignMutation.isPending}
                  onClick={() => assignMutation.mutate(assigneeId as number)}
                >
                  Assign
                </Button>
              </Stack>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Admission milestones
            </Typography>
            {milestoneError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setMilestoneError(null)}>
                {milestoneError}
              </Alert>
            )}
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <TextField
                label="Offer Letter date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                disabled={!canEditMilestones}
                value={offerLetterDate}
                onChange={(e) => setOfferLetterDate(e.target.value)}
                helperText={offerLetterDate ? 'Completed' : 'Pending'}
              />
              <TextField
                label="Invitation (COE) number"
                size="small"
                disabled={!canEditMilestones}
                value={coeNumber}
                onChange={(e) => setCoeNumber(e.target.value)}
              />
              <TextField
                label="Invitation (COE) date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                disabled={!canEditMilestones}
                value={coeDate}
                onChange={(e) => setCoeDate(e.target.value)}
                helperText={coeDate ? 'Completed' : 'Pending'}
              />
            </Stack>
            {canEditMilestones && (
              <Button
                size="small"
                variant="outlined"
                disabled={milestoneMutation.isPending}
                onClick={() => milestoneMutation.mutate()}
              >
                {milestoneMutation.isPending ? 'Saving…' : 'Save milestones'}
              </Button>
            )}
          </Box>

          {(canEditMilestones || canManageTravel) && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Travel &amp; Departure
              </Typography>
              {travelError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setTravelError(null)}>
                  {travelError}
                </Alert>
              )}
              {travelProfileQuery.isLoading && <CircularProgress size={20} />}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Visa
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <TextField
                  select
                  label="Visa status"
                  size="small"
                  sx={{ minWidth: 160 }}
                  disabled={!canEditMilestones}
                  value={visaStatusId}
                  onChange={(e) => setVisaStatusId(e.target.value ? Number(e.target.value) : '')}
                >
                  <MenuItem value="">—</MenuItem>
                  {visaStatusesQuery.data?.map((v) => (
                    <MenuItem key={v.id as number} value={v.id as number}>
                      {String(v.name)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Approval date"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  disabled={!canEditMilestones}
                  value={visaApprovalDate}
                  onChange={(e) => setVisaApprovalDate(e.target.value)}
                />
                <TextField
                  label="Expiry date"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  disabled={!canEditMilestones}
                  value={visaExpiryDate}
                  onChange={(e) => setVisaExpiryDate(e.target.value)}
                />
                <TextField
                  label="Visa remarks"
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  disabled={!canEditMilestones}
                  value={visaRemarks}
                  onChange={(e) => setVisaRemarks(e.target.value)}
                />
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Flight
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <TextField
                  label="Airline"
                  size="small"
                  disabled={!canManageTravel}
                  value={travelForm.airline ?? ''}
                  onChange={(e) => setTravelForm((f) => ({ ...f, airline: e.target.value }))}
                />
                <TextField
                  label="Flight number"
                  size="small"
                  disabled={!canManageTravel}
                  value={travelForm.flightNumber ?? ''}
                  onChange={(e) => setTravelForm((f) => ({ ...f, flightNumber: e.target.value }))}
                />
                <TextField
                  label="Departure airport"
                  size="small"
                  disabled={!canManageTravel}
                  value={travelForm.departureAirport ?? ''}
                  onChange={(e) => setTravelForm((f) => ({ ...f, departureAirport: e.target.value }))}
                />
                <TextField
                  label="Arrival airport"
                  size="small"
                  disabled={!canManageTravel}
                  value={travelForm.arrivalAirport ?? ''}
                  onChange={(e) => setTravelForm((f) => ({ ...f, arrivalAirport: e.target.value }))}
                />
                <TextField
                  label="Flight date/time"
                  type="datetime-local"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  disabled={!canEditMilestones}
                  value={departureDateTime}
                  onChange={(e) => setDepartureDateTime(e.target.value)}
                  helperText="Same as Departure date below"
                />
                <TextField
                  label="Booking reference"
                  size="small"
                  disabled={!canManageTravel}
                  value={travelForm.bookingReference ?? ''}
                  onChange={(e) => setTravelForm((f) => ({ ...f, bookingReference: e.target.value }))}
                />
                <TextField
                  select
                  label="Flight status"
                  size="small"
                  sx={{ minWidth: 140 }}
                  disabled={!canManageTravel}
                  value={travelForm.flightStatus ?? 'PENDING'}
                  onChange={(e) =>
                    setTravelForm((f) => ({ ...f, flightStatus: e.target.value as TravelDetailInput['flightStatus'] }))
                  }
                >
                  {FLIGHT_STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s.charAt(0) + s.slice(1).toLowerCase()}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Departure
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <TextField
                  label="Departure date"
                  type="datetime-local"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  disabled={!canEditMilestones}
                  value={departureDateTime}
                  onChange={(e) => setDepartureDateTime(e.target.value)}
                />
                <TextField
                  select
                  label="Departure status"
                  size="small"
                  sx={{ minWidth: 140 }}
                  disabled={!canManageTravel}
                  value={travelForm.departureStatus ?? 'PLANNED'}
                  onChange={(e) =>
                    setTravelForm((f) => ({
                      ...f,
                      departureStatus: e.target.value as TravelDetailInput['departureStatus'],
                    }))
                  }
                >
                  {DEPARTURE_STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s.charAt(0) + s.slice(1).toLowerCase()}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!travelForm.pickupRequired}
                      disabled={!canManageTravel}
                      onChange={(e) => setTravelForm((f) => ({ ...f, pickupRequired: e.target.checked }))}
                    />
                  }
                  label="Pickup required"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!travelForm.pickupAssigned}
                      disabled={!canManageTravel}
                      onChange={(e) => setTravelForm((f) => ({ ...f, pickupAssigned: e.target.checked }))}
                    />
                  }
                  label="Pickup assigned"
                />
                <TextField
                  label="Pickup / transfer assigned to"
                  size="small"
                  fullWidth
                  disabled={!canManageTravel}
                  value={travelForm.pickupAssignedTo ?? ''}
                  onChange={(e) => setTravelForm((f) => ({ ...f, pickupAssignedTo: e.target.value }))}
                />
                <TextField
                  label="Pickup / transfer notes"
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  disabled={!canManageTravel}
                  value={travelForm.pickupNotes ?? ''}
                  onChange={(e) => setTravelForm((f) => ({ ...f, pickupNotes: e.target.value }))}
                />
                <TextField
                  label="Departure remarks"
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  disabled={!canManageTravel}
                  value={travelForm.departureRemarks ?? ''}
                  onChange={(e) => setTravelForm((f) => ({ ...f, departureRemarks: e.target.value }))}
                />
              </Stack>

              <Button
                size="small"
                variant="outlined"
                disabled={travelDetailsMutation.isPending}
                onClick={() => travelDetailsMutation.mutate()}
              >
                {travelDetailsMutation.isPending ? 'Saving…' : 'Save Travel Details'}
              </Button>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Document checklist
            </Typography>
            {checklistQuery.isLoading && <CircularProgress size={20} />}
            {checklistQuery.data?.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No document requirements configured for this course.
              </Typography>
            )}
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {checklistQuery.data?.map((item) => (
                <Chip
                  key={item.documentTypeId}
                  size="small"
                  icon={item.satisfied ? <CheckCircleIcon /> : <CancelIcon />}
                  color={item.satisfied ? 'success' : item.isMandatory ? 'error' : 'default'}
                  label={`${item.documentTypeName}${item.isMandatory ? '' : ' (optional)'}`}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Ready to Fly eligibility
            </Typography>
            {eligibilityQuery.isLoading && <CircularProgress size={20} />}
            {eligibilityQuery.data && (
              <Alert
                icon={<FlightTakeoffIcon />}
                severity={eligibilityQuery.data.eligible ? 'success' : 'warning'}
              >
                {eligibilityQuery.data.eligible ? (
                  'Required payments completed, required documents approved and visa Approved — eligible for Ready to Fly.'
                ) : (
                  <Stack spacing={0.5}>
                    {eligibilityQuery.data.reasons.map((reason, i) => (
                      <Typography key={i} variant="body2">
                        • {reason}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Alert>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Remarks
            </Typography>
            {canRemark && (
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Add a remark…"
                  value={remarkText}
                  onChange={(e) => setRemarkText(e.target.value)}
                />
                <Button
                  variant="outlined"
                  disabled={!remarkText.trim() || addRemarkMutation.isPending}
                  onClick={() => addRemarkMutation.mutate()}
                >
                  Add
                </Button>
              </Stack>
            )}
            {remarksQuery.data?.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No remarks yet.
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Timeline
            </Typography>
            {timelineQuery.isLoading && <CircularProgress size={20} />}
            {timelineQuery.data?.length === 0 && (
              <Alert severity="info">No activity yet.</Alert>
            )}
            <Stack spacing={1}>
              {timelineQuery.data?.map((entry, i) => (
                <TimelineItem key={i} entry={entry} />
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
